import { Button, Input } from '@root/components'
import { useBusinessContext } from '@root/context'
import {
  IElsterCertificateUploadValues,
  uploadElsterCertificate
} from '@root/query/integrations/elster'
import { firstApiErrorMessage, getApiFieldErrors, setAPIErrors } from '@utils'
import { fileToBase64 } from '@utils/fileToBase64'
import cn from 'classnames'
import React from 'react'
import { useDropzone } from 'react-dropzone'
import { get, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaFileAlt, FaUpload } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface ElsterCertificateFormValues {
  pin: string
  label?: string
}

interface Props {
  onUploaded?: () => void
}

const unwrapMutationError = (
  error: unknown
): { status?: number; data?: Record<string, unknown> } => {
  const payload = error as {
    status?: number
    data?: Record<string, unknown>
    response?: { status?: number; data?: Record<string, unknown> }
  }
  if (payload?.response?.status) {
    return { status: payload.response.status, data: payload.response.data }
  }
  return { status: payload?.status, data: payload?.data }
}

const stripDataUrlPrefix = (dataUrl: string): string => {
  const marker = 'base64,'
  const index = dataUrl.indexOf(marker)
  return index >= 0 ? dataUrl.slice(index + marker.length) : dataUrl
}

const ElsterSettingsForm: React.FC<Props> = ({ onUploaded }) => {
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const [file, setFile] = React.useState<File | null>(null)
  const [fileError, setFileError] = React.useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors }
  } = useForm<ElsterCertificateFormValues>()

  const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    noDragEventsBubbling: true,
    accept: '.pfx,.p12,application/x-pkcs12',
    onDrop: acceptedFiles => {
      setFile(acceptedFiles[0] ?? null)
      setFileError(null)
    }
  })

  const uploadMutation = useMutation({
    mutationFn: (data: IElsterCertificateUploadValues) => uploadElsterCertificate({ slug }, data),
    ...{
      onSuccess: () => {
        reset()
        setFile(null)
        setFileError(null)
        queryClient.invalidateQueries({ queryKey: [slug, 'elster_certificate_status'] })
        queryClient.invalidateQueries({ queryKey: [slug, 'has_elster_certificate'] })
        toast.success(t('settings.elsterSettings.form.success'))
        onUploaded?.()
      },
      onError: error => {
        const { status, data } = unwrapMutationError(error)
        if (status !== 400) {
          toast.error((typeof data?.message === 'string' && data.message) || t('error.oho'))
          return
        }

        const fieldErrors = getApiFieldErrors(data)
        if (fieldErrors) {
          const { pfx_b64: pfxFieldError, ...restFieldErrors } = fieldErrors
          const hasOtherFieldErrors = Object.keys(restFieldErrors).length > 0
          if (hasOtherFieldErrors) {
            setAPIErrors(restFieldErrors, setError)
          }
          const pfxMessage = firstApiErrorMessage(pfxFieldError)
          if (pfxMessage) {
            setFileError(pfxMessage)
          }
          if (pfxMessage || hasOtherFieldErrors) {
            return
          }
        }

        const fallback =
          (typeof data?.message === 'string' && data.message.trim()) ||
          firstApiErrorMessage(data?.detail)
        if (fallback) {
          setFileError(fallback)
          return
        }
        toast.error(t('error.oho'))
      }
    }
  })

  const onSubmit = async (data: ElsterCertificateFormValues) => {
    if (!file) {
      setFileError(t('settings.elsterSettings.form.certificate.missing'))
      return
    }

    setFileError(null)
    const dataUrl = await fileToBase64(file)
    uploadMutation.mutate({
      pfx_b64: stripDataUrlPrefix(dataUrl),
      pin: data.pin,
      label: data.label || file.name
    })
  }

  const dropzoneError = fileError

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Dropzone
        className={cn({ isDragActive, isInvalid: Boolean(dropzoneError) })}
        {...getRootProps()}
        onClick={() => open()}
      >
        <input {...getInputProps()} />
        <FaFileAlt />
        <DropzoneTitle>
          {file ? file.name : t('settings.elsterSettings.form.certificate.dropTitle')}
        </DropzoneTitle>
        <Button type="button" size="sm" onClick={() => open()}>
          {t('settings.elsterSettings.form.certificate.select')}
        </Button>
        <DropzoneInfo>{t('settings.elsterSettings.form.certificate.info')}</DropzoneInfo>
      </Dropzone>
      {dropzoneError && <DropzoneError>{dropzoneError}</DropzoneError>}

      <FieldGrid>
        <Input
          label={t('settings.elsterSettings.form.pin.label')}
          info={t('settings.elsterSettings.form.pin.info')}
          type="password"
          autoComplete="off"
          {...register('pin', {
            required: { value: true, message: t('validation.required') }
          })}
          required
          errors={get(errors, 'pin')}
        />

        <Input
          label={t('settings.elsterSettings.form.label.label')}
          info={t('settings.elsterSettings.form.label.info')}
          placeholder={t('settings.elsterSettings.form.label.placeholder')}
          {...register('label')}
          errors={get(errors, 'label')}
        />
      </FieldGrid>

      <SubmitRow>
        <SubmitInfo>{t('settings.elsterSettings.form.storageInfo')}</SubmitInfo>
        <Button
          type="submit"
          intent="success"
          icon={<FaUpload />}
          disabled={uploadMutation.isPending}
          showSpinner={uploadMutation.isPending}
        >
          {t('settings.elsterSettings.form.send')}
        </Button>
      </SubmitRow>
    </form>
  )
}

export default ElsterSettingsForm

const Dropzone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.defaultHover};
  text-align: center;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;

  > svg {
    color: ${({ theme }) => theme.colors.iconGray};
    width: ${({ theme }) => theme.fontSize.xl}rem;
    height: ${({ theme }) => theme.fontSize.xl}rem;
  }

  &.isDragActive {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &.isInvalid {
    border-color: ${({ theme }) => theme.colors.error};
  }

  > button {
    margin: ${({ theme }) => theme.spacing.xs}rem 0;
  }
`

const DropzoneTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutralBlack};
  overflow-wrap: anywhere;
`

const DropzoneInfo = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const DropzoneError = styled.span`
  display: block;
  margin: -${({ theme }) => theme.spacing.sm}rem 0 ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.error};
`

const FieldGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  @media only screen and (min-width: ${({ theme }) => theme.breakpoint.md}) {
    grid-template-columns: 1fr 1fr;
  }
`

const SubmitRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;

  > button {
    align-self: flex-end;
  }
`

const SubmitInfo = styled.span`
  width: 100%;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`
